import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, BadgeCheck, Upload, Loader2, AlertTriangle, RefreshCw, IdCard, Camera, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail, API } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";

const STATUS_META = {
  unverified: { label: "Not started", color: "#f59e0b", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.30)" },
  pending: { label: "Under review", color: "#60a5fa", bg: "rgba(96,165,250,0.10)", border: "rgba(96,165,250,0.30)" },
  verified: { label: "Verified", color: "#21c46e", bg: "rgba(33,196,110,0.12)", border: "rgba(33,196,110,0.35)" },
  rejected: { label: "Rejected", color: "#f87171", bg: "rgba(248,113,113,0.10)", border: "rgba(248,113,113,0.30)" },
};

function FilePicker({ label, file, onPick, hint, icon: Icon, testid }) {
  const ref = useRef(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    if (file.type === "application/pdf") { setPreview("pdf"); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-white/55 mb-2">{label}</div>
      <div
        onClick={() => ref.current?.click()}
        data-testid={testid}
        className="relative cursor-pointer rounded-xl border-2 border-dashed border-white/15 hover:border-[#21c46e]/60 transition-colors bg-[#101824] overflow-hidden"
        style={{ minHeight: 180 }}
      >
        {preview && preview !== "pdf" ? (
          <img src={preview} alt={label} className="w-full h-44 object-cover" />
        ) : preview === "pdf" ? (
          <div className="h-44 flex flex-col items-center justify-center text-white/70">
            <Upload size={28} />
            <span className="text-sm mt-2">PDF — {file?.name}</span>
            <span className="text-xs text-white/40 mt-1">PDF requires manual review</span>
          </div>
        ) : (
          <div className="h-44 flex flex-col items-center justify-center text-white/55 px-4 text-center">
            <Icon size={28} className="text-[#21c46e]/80" />
            <span className="text-sm font-semibold mt-2 text-white/85">Tap to upload</span>
            <span className="text-xs mt-1 leading-relaxed">{hint}</span>
          </div>
        )}
        {file && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPick(null); }}
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center"
            aria-label={`remove ${label}`}
          >
            <X size={14} />
          </button>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (f.size > 5 * 1024 * 1024) {
            toast.error("File too large — max 5 MB");
            return;
          }
          onPick(f);
        }}
      />
    </div>
  );
}

export default function KycSubmitPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/profile/kyc/status");
      setStatus(data);
    } catch {
      setStatus({ kyc_status: user?.kyc_status || "unverified" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!idFront) return toast.error("Please upload the FRONT of your NID");
    if (!idBack) return toast.error("Please upload the BACK of your NID");
    if (!selfie) return toast.error("Please upload a selfie holding the ID");
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("id_doc_front", idFront);
      form.append("id_doc_back", idBack);
      form.append("selfie", selfie);
      const { data } = await api.post("/profile/kyc/submit", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshUser();
      await loadStatus();
      setIdFront(null);
      setIdBack(null);
      setSelfie(null);
      const map = {
        verified: { fn: "success", msg: "✅ KYC verified instantly! Your account is now fully verified." },
        rejected: { fn: "error", msg: `❌ KYC rejected: ${data.reason || "did not pass automatic checks"}` },
        pending: { fn: "info", msg: "📋 Documents received — under manual review (within 24 hours)." },
      };
      const t = map[data.status] || map.pending;
      toast[t.fn](t.msg, { duration: 7000 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const currentStatus = status?.kyc_status || user?.kyc_status || "unverified";
  const meta = STATUS_META[currentStatus] || STATUS_META.unverified;
  const lastReason = status?.last_submission?.decision_reason || status?.last_submission?.ai?.reason;
  const canSubmit = currentStatus !== "verified" && currentStatus !== "pending";

  return (
    <ProfileShell title="Identity Verification (KYC)">
      <div className="py-2 pb-10 space-y-4" data-testid="kyc-submit-page">
        {/* Status hero */}
        <div
          className="rounded-2xl p-5 relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${meta.bg} 0%, rgba(10,17,25,0.7) 60%)`, border: `1px solid ${meta.border}` }}
        >
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: meta.bg, border: `1px solid ${meta.border}` }}>
              {currentStatus === "verified" ? <BadgeCheck size={24} style={{ color: meta.color }} /> : <ShieldCheck size={24} style={{ color: meta.color }} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white/55 text-xs uppercase tracking-wider">Current status</div>
              <div className="text-2xl font-extrabold mt-0.5" style={{ color: meta.color }}>{meta.label}</div>
              {lastReason && currentStatus !== "unverified" && (
                <div className="text-white/70 text-sm mt-2 leading-relaxed">{lastReason}</div>
              )}
            </div>
          </div>
        </div>

        {/* Auto-KYC explainer */}
        {canSubmit && (
          <div className="rounded-xl p-4 bg-gradient-to-br from-[#21c46e]/10 to-transparent border border-[#21c46e]/25" data-testid="kyc-explainer">
            <div className="text-[#21c46e] text-xs font-bold uppercase tracking-wider mb-2">⚡ Auto-verification</div>
            <p className="text-white/75 text-sm leading-relaxed">
              Upload a clear photo of your government ID + a selfie holding it. Our AI will <b className="text-white">instantly verify</b> your identity in seconds. No waiting in queues — over 80% of submissions are auto-approved.
            </p>
            <ul className="mt-3 space-y-1.5 text-xs text-white/55">
              <li>✓ NID / Passport / Driving License accepted</li>
              <li>✓ JPEG, PNG, WEBP up to 5 MB per file</li>
              <li>✓ Make sure all 4 corners + face are visible & not blurry</li>
            </ul>
          </div>
        )}

        {/* Submit form */}
        {canSubmit && !loading && (
          <form onSubmit={submit} className="space-y-4" data-testid="kyc-form">
            <FilePicker
              label="1. NID front page"
              file={idFront}
              onPick={setIdFront}
              hint="Front side · name, photo & ID number visible · all 4 corners visible"
              icon={IdCard}
              testid="kyc-id-front-picker"
            />
            <FilePicker
              label="2. NID back page"
              file={idBack}
              onPick={setIdBack}
              hint="Back side · address & signature visible · all 4 corners visible"
              icon={IdCard}
              testid="kyc-id-back-picker"
            />
            <FilePicker
              label="3. Selfie holding the ID"
              file={selfie}
              onPick={setSelfie}
              hint="Hold the ID next to your face · good lighting · no glasses/hat"
              icon={Camera}
              testid="kyc-selfie-picker"
            />
            <button
              type="submit"
              disabled={submitting || !idFront || !idBack || !selfie}
              data-testid="kyc-submit-btn"
              className="w-full h-12 rounded-lg text-white font-extrabold tracking-wide flex items-center justify-center gap-2 disabled:opacity-60 transition-all active:scale-[0.99]"
              style={{
                background: "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
                boxShadow: "0 6px 20px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              {submitting ? <><Loader2 size={18} className="animate-spin" /> AI verifying… (10–20s)</> : <><BadgeCheck size={18} /> Submit for verification</>}
            </button>
          </form>
        )}

        {currentStatus === "pending" && (
          <div className="rounded-xl p-4 bg-blue-500/8 border border-blue-400/30 flex items-start gap-3" data-testid="kyc-pending">
            <Loader2 size={20} className="animate-spin text-blue-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-blue-100 font-semibold text-sm">Your documents are with our reviewers</div>
              <p className="text-blue-200/80 text-xs mt-1 leading-relaxed">We'll notify you within 24 hours. You can close this page — you don't need to wait.</p>
            </div>
          </div>
        )}

        {currentStatus === "rejected" && !canSubmit && (
          <div className="rounded-xl p-4 bg-rose-500/8 border border-rose-400/30 flex items-start gap-3">
            <AlertTriangle size={20} className="text-rose-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-rose-100 font-semibold text-sm">Verification rejected</div>
              <p className="text-rose-200/80 text-xs mt-1 leading-relaxed">{lastReason}</p>
            </div>
          </div>
        )}

        {currentStatus === "verified" && (
          <button
            type="button"
            onClick={() => navigate("/profile/verification")}
            data-testid="kyc-back-btn"
            className="w-full h-11 rounded-lg bg-white/[0.06] border border-white/10 text-white/85 hover:bg-white/[0.10]"
          >
            Back to verification
          </button>
        )}

        <p className="text-white/40 text-[11px] text-center px-4 leading-relaxed pt-2">
          🔒 Your documents are encrypted at rest and only viewed by our verification system. We never share or sell your data. {API ? "" : ""}
        </p>
      </div>
    </ProfileShell>
  );
}
